import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import notificationWebSocketService from '../services/notificationWebSocketService';

const HTTPS_WARNING_SESSION_KEY = 'push_notifications_https_warning_shown';
const HTTPS_WARNING_TEXT = 'Для уведомлений нужен HTTPS или localhost.';

/**
 * Hook для обработки push уведомлений в реальном времени
 * Подключается к WebSocket для получения уведомлений
 */
export const usePushNotifications = (options = {}) => {
  const { manageConnection = true } = options;
  const { user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [lastNotification, setLastNotification] = useState(null);
  const [error, setError] = useState('');
  const [permissionGranted, setPermissionGranted] = useState(false);

  const isHttpsWarningMessage = useCallback(
    (message) => typeof message === 'string' && message.includes(HTTPS_WARNING_TEXT),
    []
  );

  const shouldShowHttpsWarning = useCallback(() => {
    try {
      if (sessionStorage.getItem(HTTPS_WARNING_SESSION_KEY)) {
        return false;
      }
      sessionStorage.setItem(HTTPS_WARNING_SESSION_KEY, '1');
      return true;
    } catch (_storageError) {
      // Если storage недоступен, лучше показать предупреждение, чем скрыть его.
      return true;
    }
  }, []);
  
  const getSafeInternalPath = useCallback((rawUrl) => {
    if (!rawUrl || typeof rawUrl !== 'string') return null;
    const trimmed = rawUrl.trim();
    if (!trimmed.startsWith('/')) return null;
    if (trimmed.startsWith('//')) return null;
    if (trimmed.startsWith('/\\')) return null;
    return trimmed;
  }, []);

  // Запрос разрешения на показ уведомлений в браузере
  const requestNotificationPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      setError('Браузер не поддерживает уведомления');
      return false;
    }

    const currentPermission = Notification.permission;

    if (currentPermission === 'granted') {
      setPermissionGranted(true);
      return true;
    }

    if (currentPermission === 'denied') {
      setPermissionGranted(false);
      
      // Проверяем тип хоста для более информативного сообщения
      const isLocalhost = window.location.hostname === 'localhost' || 
                         window.location.hostname === '127.0.0.1';
      const isHTTPS = window.location.protocol === 'https:';
      const isIP = /^\d+\.\d+\.\d+\.\d+$/.test(window.location.hostname);
      
      let errorMessage = 'Разрешение на уведомления отклонено. ';
      
      if (!isLocalhost && !isHTTPS) {
        if (!shouldShowHttpsWarning()) {
          setError('');
          return false;
        }
        errorMessage += `${HTTPS_WARNING_TEXT} `;
      } else if (isIP && !isLocalhost) {
        errorMessage += 'Уведомления не работают с IP адресами, нужен домен. ';
      }
      
      errorMessage += 'Включите уведомления в настройках браузера для этого сайта.';
      setError(errorMessage);
      return false;
    }

    if (currentPermission === 'default') {
      try {
        const permission = await Notification.requestPermission();
        const granted = permission === 'granted';
        setPermissionGranted(granted);
        
        if (!granted) {
          setError('Разрешение на уведомления не предоставлено. Попробуйте еще раз или проверьте настройки браузера.');
        } else {
          setError(''); // Очищаем ошибку при успешном получении разрешения
        }
        
        return granted;
      } catch (err) {
        console.error('Error requesting notification permission:', err);
        setError('Ошибка запроса разрешения на уведомления. Возможно, ваш браузер блокирует уведомления для этого сайта.');
        return false;
      }
    }

    return false;
  }, [shouldShowHttpsWarning]);

  // Показ нативного уведомления браузера
  const showBrowserNotification = useCallback((notification) => {
    if (!permissionGranted || !('Notification' in window)) {
      return;
    }

    const title =
      notification?.title ??
      notification?.subject ??
      'Уведомление';
    const body =
      notification?.body ??
      notification?.content ??
      '';
    const data = notification?.data;

    try {
      const browserNotification = new Notification(title, {
        body: body || undefined,
        icon: '/images/favicon-32x32.png',
        badge: '/images/favicon-32x32.png',
        tag: data?.notification_uid || 'push-notification',
        requireInteraction: false,
        silent: false
      });

      setTimeout(() => {
        browserNotification.close();
      }, 5000);

      browserNotification.onclick = () => {
        window.focus();
        browserNotification.close();

        const safeUrl = getSafeInternalPath(data?.url);
        if (safeUrl) {
          window.location.assign(safeUrl);
        } else if (data?.url && process.env.NODE_ENV !== 'production') {
          console.warn('Blocked unsafe notification URL redirect');
        }
      };
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Native Notification failed:', e);
      }
    }
  }, [permissionGranted, getSafeInternalPath]);

  const handleIncomingNotification = useCallback((notificationPayload, data) => {
    if (!notificationPayload) {
      return;
    }

    const title =
      notificationPayload.title ??
      notificationPayload.subject ??
      'Новое уведомление';
    const body =
      notificationPayload.body ??
      notificationPayload.content ??
      '';

    const normalizedNotification = {
      ...notificationPayload,
      title,
      body,
      timestamp: data?.timestamp || new Date().toISOString(),
      profile_id: data?.profile_id
    };

    setLastNotification(normalizedNotification);
    showBrowserNotification(normalizedNotification);

    window.dispatchEvent(
      new CustomEvent('notification_received', {
        detail: { notification: notificationPayload }
      })
    );
  }, [showBrowserNotification]);

  // Стабильная ссылка на обработчик: подписка на WS не должна пересоздаваться
  // при смене permissionGranted (иначе release/acquire рвут единственное соединение).
  const handleIncomingNotificationRef = useRef(handleIncomingNotification);
  useEffect(() => {
    handleIncomingNotificationRef.current = handleIncomingNotification;
  }, [handleIncomingNotification]);

  // Подключение к WebSocket
  const connect = useCallback(() => {
    void notificationWebSocketService.connect();
  }, []);

  // Отправка ping для heartbeat
  const sendPing = useCallback(() => {
    notificationWebSocketService.ping();
  }, []);

  // Отключение от WebSocket
  const disconnect = useCallback(() => {
    notificationWebSocketService.disconnect();
    setError('');
  }, []);

  // Инициализация при монтировании компонента
  useEffect(() => {
    // Проверяем текущее состояние разрешений без запроса
    if ('Notification' in window) {
      const currentPermission = Notification.permission;
      setPermissionGranted(currentPermission === 'granted');
      
      if (currentPermission === 'denied') {
        // Проверяем тип хоста для более информативного сообщения
        const isLocalhost = window.location.hostname === 'localhost' || 
                           window.location.hostname === '127.0.0.1';
        const isHTTPS = window.location.protocol === 'https:';
        const isIP = /^\d+\.\d+\.\d+\.\d+$/.test(window.location.hostname);
        
        let errorMessage = 'Разрешение на уведомления отклонено. ';
        
        if (!isLocalhost && !isHTTPS) {
          if (!shouldShowHttpsWarning()) {
            // Браузерные push denied не должны блокировать in-app WebSocket канал.
            setError('');
          } else {
            errorMessage += `${HTTPS_WARNING_TEXT} `;
            errorMessage += 'Включите уведомления в настройках браузера.';
            setError(errorMessage);
          }
        } else if (isIP && !isLocalhost) {
          errorMessage += 'Уведомления не работают с IP адресами, нужен домен. ';
          errorMessage += 'Включите уведомления в настройках браузера.';
          setError(errorMessage);
        } else {
          errorMessage += 'Включите уведомления в настройках браузера.';
          setError(errorMessage);
        }
      }
      // permission === 'default': WebSocket/in-app уведомления работают без OS-разрешения.
    } else {
      // Safari iOS и др. без Notification API — in-app WebSocket всё равно работает.
      setPermissionGranted(false);
    }

    // WebSocket тикет идёт по Bearer из localStorage; user из контекста может ещё не быть
    // (bootstrap /me, истёкший JWT при первом кадре) — не блокируем подключение отсутствием user.
    if (!localStorage.getItem('token')) {
      try {
        sessionStorage.removeItem(HTTPS_WARNING_SESSION_KEY);
      } catch {
        // ignore
      }
      return undefined;
    }

    if (!manageConnection) {
      return undefined;
    }

    const unsubscribeStatus = notificationWebSocketService.onStatusChange((status) => {
      setIsConnected(status === 'websocket');
      if (status === 'connecting') {
        setError((prevError) => (isHttpsWarningMessage(prevError) ? prevError : ''));
        setIsConnected(false);
        return;
      }
      if (status === 'websocket') {
        setError((prevError) => (isHttpsWarningMessage(prevError) ? prevError : ''));
      }
      if (status === 'api' && !notificationWebSocketService.isDiagnosticsPaused()) {
        setError('Ошибка подключения к серверу уведомлений');
      }
      if (status === 'disconnected') {
        setIsConnected(false);
      }
    });

    const unsubscribeMessages = notificationWebSocketService.subscribeAll((data) => {
      if (!data || typeof data !== 'object' || !data.type) {
        return;
      }

      switch (data.type) {
        case 'connection_established':
          setError('');
          notificationWebSocketService.send({
            type: 'connection_confirmed',
            timestamp: new Date().toISOString()
          });
          break;
        case 'pong':
          break;
        case 'push_notification':
          handleIncomingNotificationRef.current(data.notification, data);
          break;
        case 'test_notification':
          handleIncomingNotificationRef.current(
            {
              type: data.type,
              title: data.title,
              body: data.body,
              data: data.data
            },
            data
          );
          break;
        case 'notification_count_update':
          window.dispatchEvent(new CustomEvent('notification_counts_refresh'));
          break;
        default:
      }
    });

    const releaseConnection = notificationWebSocketService.acquireConnection();

    // Очистка при размонтировании
    return () => {
      releaseConnection();
      unsubscribeMessages();
      unsubscribeStatus();
    };
  }, [user, shouldShowHttpsWarning, isHttpsWarningMessage, manageConnection]);

  // Автоматический heartbeat каждые 30 секунд
  useEffect(() => {
    let heartbeatInterval;
    
    if (isConnected) {
      heartbeatInterval = setInterval(() => {
        sendPing();
      }, 30000); // 30 секунд
    }
    
    return () => {
      if (heartbeatInterval) {
        clearInterval(heartbeatInterval);
      }
    };
  }, [isConnected, sendPing]);

  return {
    isConnected,
    lastNotification,
    error,
    permissionGranted,
    requestNotificationPermission,
    connect,
    disconnect,
    sendPing,
    clearError: () => setError(''),
    clearLastNotification: () => setLastNotification(null)
  };
}; 